import React, { useEffect, useMemo, useState } from 'react'
import { Plus, Save, Trash2, Undo2 } from 'lucide-react'
import * as api from '../api'
import { useHR } from '../../hr/HRContext'
import {
  applyFilters, can, Chip, COL, Confirm, EditModal, ErrorBox, esc, exportRows, Filters, invalidate, Kpi, Loading, n2, naira, plabel, printHtml, printRows, PyHeader, Sel,
  Stat, sum, Toolbar, usePyData, usePyMeta, usePyPeriod, Workbook,
} from '../py'
import { Select } from '../../hr/components/Controls'

// Stephanet's Benefits Setup, Benefit Enrolment, Entitlements, Benefits Due and Benefits Statement (custom/pay15/Benefits.jsx), ported
// unchanged; the statement takes the bank details from the engine rows.

const VIA = ['WITH SALARY', 'SEPARATELY', 'TO PROVIDER']
const BASIS = ['FIXED', '% OF BASIC', '% OF MONTHLY PAY', 'SITE ALLOWANCE', 'HAZARD ALLOWANCE', 'OUTSTATION ALLOWANCE']
const FREQ = ['MONTHLY', 'QUARTERLY', 'BI-ANNUAL', 'ANNUAL', 'ONE-OFF']
const RULE = ['ALL', 'GRADE FLAG', 'SITE', 'MANUAL']
export const entOf = (r, slot) => { const e = r.ent.find(x => x[0] === slot); return e ? { entitled: e[1], per: e[2], annual: e[3], due: e[4] } : { entitled: 0, per: 0, annual: 0, due: 0 } }

function useMonth() {
  const [meta] = usePyMeta()
  const [period, setPeriod] = usePyPeriod(meta)
  const [d, err, reload] = usePyData(period)
  return { meta, period, setPeriod, d, err, reload }
}

// ------------------------------------------------------------------ BENEFITS SETUP

export function BenefitsSetup() {
  const { notify } = useHR()
  const { meta, period, setPeriod, d, err, reload } = useMonth()
  const [edit, setEdit] = useState(null)
  const [del, setDel] = useState(null)
  if (err) return <ErrorBox error={err} />
  if (!d) return <Loading what="benefits" />
  const rows = d.benefits
  const cols = [
    { key: 'slot', label: 'Slot', kind: 'num', w: 50 }, { key: 'name', label: 'Benefit / allowance', w: 180 }, { key: 'active', label: 'Active', w: 60, get: r => (r.active ? 'Y' : 'N') },
    { key: 'paid_via', label: 'How it is paid', w: 120 }, { key: 'basis', label: 'Basis', w: 150 },
    { key: 'amount', label: 'Amount or rate', w: 110, get: r => (r.basis.startsWith('%') ? `${+(r.amount * 100).toFixed(3)}%` : r.basis === 'FIXED' ? n2(r.amount) : '(from project)') },
    { key: 'frequency', label: 'Frequency', w: 100 }, { key: 'first_month', label: 'First pay month', kind: 'num', w: 80 }, { key: 'oneoff_date', label: 'One-off month', w: 100, get: r => (r.oneoff_date ? plabel(r.oneoff_date.slice(0, 7)) : '') },
    { key: 'times', label: 'Times a year', kind: 'num', w: 70 }, { key: 'rule', label: 'Who is entitled', w: 100 }, { key: 'grade_flag', label: 'Grade flag column', w: 120 },
    { key: 'structures', label: 'Pay structures', w: 100 }, { key: 'taxable', label: 'Taxable', w: 70, get: r => (r.taxable ? 'Y' : 'N') },
    { key: 'due_now', label: 'Due this month?', w: 90, get: r => (r.due_now ? 'YES' : 'NO') }, { key: 'staff_entitled', label: 'Staff entitled', kind: 'num', sum: true },
    { key: 'annual_all', label: 'Annual value — all staff', kind: 'money' }, { key: 'due_all', label: 'Due this month — all staff', kind: 'money' },
    { key: 'months', label: 'Paid in which months', w: 150 }, { key: 'notes', label: 'Notes', w: 160 },
  ]
  const fields = [
    { k: 'name', label: 'Benefit / allowance', req: true }, { k: 'active', label: 'Active', type: 'check' },
    { k: 'paid_via', label: 'How it is paid', type: 'select', options: VIA, blank: false, hint: 'With salary = taxed and on the payslip · Separately = paid on its own · To provider = HMO, insurer…' },
    { k: 'basis', label: 'Basis', type: 'select', options: BASIS, blank: false },
    { k: 'amount', label: 'Amount (₦) or rate (fraction, 0.1 = 10%)', type: 'number', show: f => !['SITE ALLOWANCE', 'HAZARD ALLOWANCE', 'OUTSTATION ALLOWANCE'].includes(f.basis) },
    { k: 'frequency', label: 'Frequency', type: 'select', options: FREQ, blank: false },
    { k: 'first_month', label: 'First pay month (1–12)', type: 'number', show: f => ['QUARTERLY', 'BI-ANNUAL', 'ANNUAL'].includes(f.frequency) },
    { k: 'oneoff_date', label: 'One-off pay month', type: 'date', show: f => f.frequency === 'ONE-OFF', req: false },
    { k: 'rule', label: 'Who is entitled (rule)', type: 'select', options: RULE, blank: false, hint: 'ALL · GRADE FLAG (grade column says YES) · SITE (home site has an amount) · MANUAL (Y in Benefit Enrolment)' },
    { k: 'grade_flag', label: 'Grade flag column', type: 'select', options: meta?.flag_headers || [], show: f => f.rule === 'GRADE FLAG' },
    { k: 'structures', label: 'Applies to pay structures', hint: 'ALL, or e.g. GROSS DAILY' }, { k: 'taxable', label: 'Taxable (when paid with salary)', type: 'check' },
    { k: 'notes', label: 'Notes', type: 'textarea' },
  ]
  const done = async r => { notify(r.message); setEdit(null); setDel(null); invalidate(); reload() }
  return (
    <div className="ps py">
      <PyHeader title="Benefits Setup" sub="Each benefit: amount, how often it is paid and who is entitled. Due benefits flow into payroll and Financial automatically." meta={meta} period={period}>
        <Toolbar onExport={() => exportRows('benefits-setup.csv', cols, rows)} onPrint={() => printRows('Benefits Setup', plabel(period), cols, rows)}>
          {can(meta, 'py-benefits-setup', 'create') && <button className="mds-run" onClick={() => setEdit({ active: true, paid_via: 'WITH SALARY', basis: 'FIXED', frequency: 'MONTHLY', rule: 'ALL', structures: 'ALL', taxable: true, first_month: 1 })}>
            <Plus size={14} /> Add Benefit</button>}
        </Toolbar>
      </PyHeader>
      <Filters meta={meta} period={period} setPeriod={setPeriod} f={{}} setF={() => {}} show={[]} />
      <div className="ps-kpis four">
        <Kpi label="Benefits" value={rows.length} note={`${rows.filter(b => b.active).length} active`} />
        <Kpi label="Annual Value — All Staff" value={naira(sum(rows, 'annual_all'))} tone="indigo" i={1} />
        <Kpi label="Due This Month" value={naira(sum(rows, 'due_all'))} tone="teal" i={2} note={`${rows.filter(b => b.due_now).length} benefit(s) due`} />
        <Kpi label="Paid Separately This Month" value={naira(sum(rows.filter(b => b.paid_via === 'SEPARATELY'), 'due_all'))} tone="amber" i={3} />
      </div>
      <Workbook cols={cols} rows={rows} frozen={2} onRow={can(meta, 'py-benefits-setup') ? r => setEdit({ ...r }) : undefined} empty="No benefits set up." />
      <p className="muted small py-foot">Annual value = amount × times a year (Monthly ×12, Quarterly ×4, Bi-annual ×2, Annual ×1, One-off ×1).</p>
      {edit && <EditModal title={edit.id ? `Edit ${edit.name}` : 'Add Benefit'} value={edit} fields={fields} onClose={() => setEdit(null)}
        onSave={async v => done(await api.saveBenefitSheet(v.id, v))}>
        {v => v.id && can(meta, 'py-benefits-setup', 'delete') ? <button className="mdr-btn danger py-del" onClick={() => setDel(v)}><Trash2 size={13} /> Delete benefit</button> : null}
      </EditModal>}
      {del && <Confirm title="Delete benefit" text={`Delete ${del.name}? Its enrolment exceptions are removed too.`} danger yes="Delete" onClose={() => setDel(null)}
        onYes={async () => done({ ...(await api.deleteBenefit(del.id)), message: `${del.name} deleted` })} />}
    </div>
  )
}

// ------------------------------------------------------------------ BENEFIT ENROLMENT

export function Enrolment() {
  const { notify } = useHR()
  const { meta, period, d, err, reload } = useMonth()
  const [choices, setChoices] = useState(null)
  const [draft, setDraft] = useState({})
  const [f, setF] = useState({ q: '', structure: '', site: '' })
  const [busy, setBusy] = useState(false)
  const load = () => api.enrolmentChoices().then(x => { setChoices(Object.fromEntries(x.choices.map(c => [`${c.employee_id}:${c.benefit_id}`, c.choice]))); setDraft({}) })
  useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <ErrorBox error={err} />
  if (!d || !choices) return <Loading what="benefit enrolment" />
  const rows = applyFilters(d.rows, f)
  const edit = can(meta, 'py-enrolment')
  const val = (e, b) => draft[`${e}:${b}`] ?? choices[`${e}:${b}`] ?? ''
  const save = async () => {
    setBusy(true)
    try {
      const changes = Object.entries(draft).map(([k, choice]) => { const [employee_id, benefit_id] = k.split(':'); return { employee_id, benefit_id, choice } })
      const r = await api.saveEnrolmentChoices(changes); notify(r.message); invalidate(); reload(); load()
    } catch (e) { notify(e.message, 'bad') } finally { setBusy(false) }
  }
  const n = Object.keys(draft).length
  return (
    <div className="ps py">
      <PyHeader title="Benefit Enrolment" sub="Per-person exceptions: Y = always entitled, N = never entitled (overrides the rule in Benefits Setup). Leave blank to follow the rule." meta={meta} period={period}>
        {edit && <button className="mdr-btn" disabled={!n} onClick={() => setDraft({})}><Undo2 size={14} /> Undo</button>}
        {edit && <button className="mds-run" disabled={busy || !n} onClick={save}><Save size={14} /> Save{n ? ` (${n})` : ''}</button>}
      </PyHeader>
      <Filters meta={meta} f={f} setF={setF} />
      <div className="ps-wb" style={{ maxHeight: 620 }}>
        <table className="ps-table py-matrix">
          <thead><tr><th className="fz" style={{ left: 0, minWidth: 96 }}>Employee ID</th><th className="fz" style={{ left: 96, minWidth: 170 }}>Name</th><th>Structure</th>
            {d.benefits.map(b => <th key={b.id} className="c" title={`${b.rule}${b.grade_flag ? ` (${b.grade_flag})` : ''} · ${b.paid_via}`}>{b.name}<small>{b.rule}</small></th>)}</tr></thead>
          <tbody>{rows.map(r => <tr key={r.id}>
            <td className="fz" style={{ left: 0, minWidth: 96 }}>{r.employee_no}</td><td className="fz" style={{ left: 96, minWidth: 170 }}>{r.name}</td><td>{r.structure}</td>
            {d.benefits.map(b => {
              const v = val(r.id, b.id)
              const ent = entOf(r, b.slot).entitled
              return <td key={b.id} className={`c py-enr ${ent ? 'ent' : ''} ${draft[`${r.id}:${b.id}`] !== undefined ? 'dirty' : ''}`} title={ent ? 'Entitled this month' : 'Not entitled'}>
                <Select disabled={!edit} value={v} onChange={e => setDraft({ ...draft, [`${r.id}:${b.id}`]: e.target.value })}><option value="">·</option><option>Y</option><option>N</option></Select></td>
            })}</tr>)}</tbody>
        </table>
      </div>
      <p className="muted small py-foot">Green cells are entitled this month (after the rule and any exception). Inactive staff are never entitled.</p>
    </div>
  )
}

// ------------------------------------------------------------------ ENTITLEMENTS

export function Entitlements() {
  const { meta, period, setPeriod, d, err } = useMonth()
  const [f, setF] = useState({ q: '', structure: '', site: '' })
  const [view, setView] = useState('entitled')
  const rows = useMemo(() => (d ? applyFilters(d.rows, f) : []), [d, f])
  if (err) return <ErrorBox error={err} />
  if (!d) return <Loading what="entitlements" />
  const bcols = d.benefits.map(b => ({ key: `b${b.slot}`, label: b.name, w: 110, kind: view === 'entitled' ? undefined : 'money',
    get: r => { const e = entOf(r, b.slot); return view === 'entitled' ? (e.entitled ? 'YES' : '-') : view === 'annual' ? e.annual : e.due } }))
  const cols = [COL.employee_no, COL.name, COL.grade, { ...COL.structure, w: 80 }, COL.site, { key: 'paid', label: 'Active', w: 60, get: r => (r.paid ? 1 : 0) },
    { key: 'basic', label: 'Monthly Basic', kind: 'money' }, { key: 'monthly_pay', label: 'Monthly pay', kind: 'money' },
    { key: 'site_allowance_amt', label: 'Site allowance', kind: 'money' }, { key: 'hazard_amt', label: 'Hazard amount', kind: 'money' }, { key: 'outstation_amt', label: 'Outstation amount', kind: 'money' },
    ...bcols,
    { key: 'ann', label: 'Annual benefits — total', kind: 'money', get: r => r.fin.ann_with_salary + r.fin.ann_separate + r.fin.ann_provider },
    { key: 'ann_t', label: 'Annual with salary (taxable)', kind: 'money', get: r => r.fin.ann_ben_tax },
    { key: 'ann_n', label: 'Annual with salary (non-taxable)', kind: 'money', get: r => r.fin.ann_ben_nontax },
    { key: 'ann_s', label: 'Annual paid separately', kind: 'money', get: r => r.fin.ann_separate }, { key: 'ann_p', label: 'Annual paid to providers', kind: 'money', get: r => r.fin.ann_provider },
    { key: 'due', label: 'Due this month — total', kind: 'money', get: r => r.ben_tax_monthly + r.ben_tax_other + r.ben_nontax + r.ben_separate + r.ben_provider },
    { key: 'ben_tax_monthly', label: 'Due: with salary, taxable, monthly', kind: 'money' }, { key: 'ben_tax_other', label: 'Due: with salary, taxable, other', kind: 'money' },
    { key: 'ben_nontax', label: 'Due: with salary, non-taxable', kind: 'money' }, { key: 'ben_separate', label: 'Due: paid separately', kind: 'money' },
    { key: 'ben_provider', label: 'Due: to providers', kind: 'money' }]
  return (
    <div className="ps py">
      <PyHeader title="Entitlements" sub="Every employee × every benefit: who is entitled, the annual value and what is due in this payroll month." meta={meta} period={period}>
        <Toolbar onExport={() => exportRows(`entitlements-${period}.csv`, cols, rows)} onPrint={() => printRows('Entitlements', plabel(period), cols, rows)} />
      </PyHeader>
      <Filters meta={meta} period={period} setPeriod={setPeriod} f={f} setF={setF}>
        <Sel label="Show" value={view} onChange={setView} all={null} options={[{ value: 'entitled', label: 'Entitled? (YES / -)' }, { value: 'annual', label: 'Annual value (₦)' }, { value: 'due', label: 'Due this month (₦)' }]} />
      </Filters>
      <Workbook cols={cols} rows={rows} frozen={2} maxHeight={620} />
    </div>
  )
}

// ------------------------------------------------------------------ BENEFITS DUE

export function BenefitsDue() {
  const { meta, period, setPeriod, d, err } = useMonth()
  const [f, setF] = useState({ q: '', structure: '', site: '' })
  const rows = useMemo(() => (d ? applyFilters(d.rows, f).filter(r => r.ent.some(e => e[4] > 0)) : []), [d, f])
  if (err) return <ErrorBox error={err} />
  if (!d) return <Loading what="benefits due" />
  const cols = [COL.employee_no, COL.name, { ...COL.structure, w: 80 }, COL.site,
    ...d.benefits.map(b => ({ key: `b${b.slot}`, label: `${b.name}${b.due_now ? '' : ' (not due)'}`, kind: 'money', w: 110, get: r => entOf(r, b.slot).due })),
    { key: 'ws', label: 'With salary', kind: 'money', get: r => r.ben_tax_monthly + r.ben_tax_other + r.ben_nontax },
    { key: 'ben_separate', label: 'Paid separately', kind: 'money' }, { key: 'ben_provider', label: 'Paid to providers', kind: 'money' },
    { key: 'tot', label: 'TOTAL DUE', kind: 'money', get: r => r.ben_tax_monthly + r.ben_tax_other + r.ben_nontax + r.ben_separate + r.ben_provider }]
  const due = d.benefits.filter(b => b.due_now)
  return (
    <div className="ps py">
      <PyHeader title="Benefits Due" sub="Everyone entitled to a benefit that falls due this payroll month." meta={meta} period={period}>
        <Toolbar onExport={() => exportRows(`benefits-due-${period}.csv`, cols, rows)} onPrint={() => printRows(`Benefits Due — ${plabel(period)}`, `${rows.length} staff`, cols, rows)} />
      </PyHeader>
      <Filters meta={meta} period={period} setPeriod={setPeriod} f={f} setF={setF} />
      <div className="py-chips">{d.benefits.map(b => <Chip key={b.id} tone={b.due_now ? 'green' : 'gray'}>{b.name}: {b.due_now ? 'due' : 'not due'}</Chip>)}</div>
      <div className="ps-kpis four">
        <Kpi label="Staff With a Benefit Due" value={rows.length} note={`${due.length} benefit(s) due this month`} />
        <Kpi label="Paid With Salary" value={naira(sum(rows, r => r.ben_tax_monthly + r.ben_tax_other + r.ben_nontax))} tone="blue" i={1} />
        <Kpi label="Paid Separately" value={naira(sum(rows, 'ben_separate'))} tone="amber" i={2} />
        <Kpi label="Paid to Providers" value={naira(sum(rows, 'ben_provider'))} tone="purple" i={3} />
      </div>
      <Workbook cols={cols} rows={rows} frozen={2} maxHeight={600} empty="No benefits due this month." />
    </div>
  )
}

// ------------------------------------------------------------------ BENEFITS STATEMENT

export function BenefitsStatement() {
  const { meta, period, setPeriod, d, err } = useMonth()
  const [f, setF] = useState({ q: '', structure: '', site: '' })
  const [show, setShow] = useState('ALL')
  const lines = useMemo(() => {
    if (!d) return []
    const out = []
    applyFilters(d.rows, f).forEach(r => d.benefits.forEach(b => {
      const e = entOf(r, b.slot)
      if (e.due > 0 && (show === 'ALL' || b.paid_via === show)) {
        out.push({ id: `${r.id}-${b.slot}`, employee_no: r.employee_no, name: r.name, department: r.department, site: r.site, structure: r.structure,
          benefit: b.name, frequency: b.frequency, via: b.paid_via, taxable: b.taxable ? 'Yes' : 'No', amount: e.due,
          bank: r.bank, account: r.account })
      }
    }))
    return out
  }, [d, f, show])
  if (err) return <ErrorBox error={err} />
  if (!d) return <Loading what="the benefits statement" />
  const cols = [{ key: 'employee_no', label: 'Employee ID', w: 96 }, { key: 'name', label: 'Name', w: 170 }, { key: 'department', label: 'Department', w: 150 },
    { key: 'site', label: 'Home site', w: 160 }, { key: 'structure', label: 'Pay structure', w: 90 }, { key: 'benefit', label: 'Benefit / allowance', w: 160 },
    { key: 'frequency', label: 'How often', w: 90 }, { key: 'via', label: 'How it is paid', w: 110 }, { key: 'taxable', label: 'Taxable', w: 70 },
    { key: 'amount', label: 'Amount (₦)', kind: 'money' }, { key: 'bank', label: 'Bank', w: 130 }, { key: 'account', label: 'Account number', w: 120 }]
  const all = d.benefits.reduce((t, b) => ({ ...t, [b.paid_via]: (t[b.paid_via] || 0) + b.due_all }), {})
  const print = () => {
    const body = `<h1>${esc(meta?.company || 'RIFITEL')} — BENEFITS STATEMENT FOR ${esc(plabel(period).toUpperCase())}</h1>
      <p class="s">Benefits and allowances due in this payroll month · period ${esc(d.period.text)} · Show: ${esc(show)}</p>
      <table><thead><tr><th>S/N</th>${cols.map(c => `<th class="${c.kind ? 'r' : ''}">${esc(c.label)}</th>`).join('')}</tr></thead>
      <tbody>${lines.map((l, i) => `<tr><td>${i + 1}</td>${cols.map(c => `<td class="${c.kind ? 'r' : ''}">${esc(c.kind ? n2(l[c.key]) : l[c.key] ?? '')}</td>`).join('')}</tr>`).join('')}</tbody>
      <tfoot><tr><td></td><td colspan="${cols.length - 3}">TOTAL IN LIST (${lines.length} lines)</td><td class="r">${n2(sum(lines, 'amount'))}</td><td colspan="2"></td></tr></tfoot></table>`
    printHtml('Benefits Statement', body)
  }
  return (
    <div className="ps py">
      <PyHeader title="Benefits Statement" sub="Printable list of benefits to pay this month — one line per person per benefit, with bank details." meta={meta} period={period}>
        <Toolbar onExport={() => exportRows(`benefits-statement-${period}.csv`, cols, lines)} onPrint={print} />
      </PyHeader>
      <Filters meta={meta} period={period} setPeriod={setPeriod} f={f} setF={setF}>
        <Sel label="Show" value={show} onChange={setShow} all={null} options={['ALL', ...VIA]} />
      </Filters>
      <div className="py-stats">
        <Stat label="Paid with salary" value={naira(all['WITH SALARY'] || 0)} tone="blue" /><Stat label="Paid separately" value={naira(all.SEPARATELY || 0)} tone="amber" />
        <Stat label="Paid to providers" value={naira(all['TO PROVIDER'] || 0)} tone="purple" /><Stat label="All benefits due" value={naira(sum(d.benefits, 'due_all'))} />
        <Stat label="Lines in this list" value={lines.length} /><Stat label="Total in list" value={naira(sum(lines, 'amount'))} tone="green" />
      </div>
      <Workbook cols={cols} rows={lines} frozen={2} maxHeight={600} empty="Nothing due for this selection." />
    </div>
  )
}

